import {mapObject, objectFromEntries} from '@benchling/js-utils';
import produce from 'immer';

import {assert} from 'Asserts';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {ParameterSettings} from 'procedures/design/step-config/ProcedureStepConfigEditorContext';
import {
  EquipmentTypedModel,
  MethodNode,
  MethodNodeWithServerIds,
  ParameterUsages,
  ProcedureDataParameterPlannedValue,
  ProcedureDataParameterSetting,
  ProcedureEquipmentPlannedValue,
  ProcedureEquipmentSetting,
  ProcedureMaterialInputPlannedValue,
  ProcedureMaterialInputSetting,
  ProcedureMaterialOutputSetting,
  ProcedureStepConfig,
  ProcedureStepGroup,
} from 'procedures/ProcedureDomainTypes';
import {ValuableProcedureServerData} from 'procedures/procedures-platform/ProcedureFlowchartTypes';
import {isMethodNode} from 'procedures/processes/MethodNodeUtils';
import {FlowchartConfigWithMetadataAndRootTaskSchema} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {isStaticTaskSchemaNodeData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {MakeFieldsRequired} from 'util/Types';

// Takes an object with a nullable ID, and fills in the ID if it's null
/** @package */
export function assignId<T extends {id: string | null; cid: string}>(
  obj: T,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object,
  options: {forceAssign?: boolean} = {}
): MakeFieldsRequired<T, 'id'> {
  // In most situations, we don't want to override an existing ID
  if (obj.id != null && !options.forceAssign) {
    return obj as MakeFieldsRequired<T, 'id'>;
  }
  return {
    ...obj,
    id: getIdFromCid(obj.cid, errorContext),
  };
}

// Note: in addition to merging CIDs into step configs, this function also clears out documentVersionUpdate,
// which was sent as a command to the server. It's spent, no longer needed. It also sets the fromId to the
// last saved document version, which we got back from the server after saving.
/** @package */
export function mergeIdsIntoProcedureStepConfig(
  stepConfig: ProcedureStepConfig,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object,
  valuableServerData: ValuableProcedureServerData
): ProcedureStepConfig {
  const stepId = getIdFromCid(stepConfig.cid, errorContext);
  const {documentId, documentVersionId = null} =
    valuableServerData.stepIdToDocumentIdAndVersionIdMap[stepId] ?? {};

  return {
    // The step will get a new ID from the server, so ensure it's assigned here
    ...assignId(stepConfig, getIdFromCid, errorContext, {forceAssign: true}),
    documentId: documentId || stepConfig.documentId,
    fromId: documentVersionId,
    documentVersionUpdate: null,
  };
}

function mergeIdsIntoStepGroup(
  stepGroup: ProcedureStepGroup,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object,
  valuableServerData: ValuableProcedureServerData
): ProcedureStepGroup {
  const {group, steps} = stepGroup;
  return {
    group,
    steps: steps.map((step, stepIndexWithinGroup) =>
      mergeIdsIntoProcedureStepConfig(
        step,
        getIdFromCid,
        {
          ...errorContext,
          stepGroup: group,
          stepIndexWithinGroup,
        },
        valuableServerData
      )
    ),
  };
}

function mergeIdsIntoAllParameterSettings(
  methodParameterSettingsNode: Pick<
    MethodNodeWithServerIds,
    'dataParameterSettings' | 'materialInputSettings' | 'materialOutputSettings' | 'equipmentSettings'
  >,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object,
  valuableServerData: ValuableProcedureServerData
): ParameterSettings {
  return {
    dataParameterSettings: methodParameterSettingsNode.dataParameterSettings.map((ps) =>
      mergeIdsIntoDataParameterSetting(ps, getIdFromCid, errorContext, valuableServerData)
    ),
    materialInputSettings: methodParameterSettingsNode.materialInputSettings.map((mis) =>
      mergeIdsIntoMaterialInputSetting(mis, getIdFromCid, errorContext, valuableServerData)
    ),
    materialOutputSettings: methodParameterSettingsNode.materialOutputSettings.map((mos) =>
      mergeIdsIntoMaterialOutputSetting(mos, getIdFromCid, errorContext, valuableServerData)
    ),
    equipmentSettings: methodParameterSettingsNode.equipmentSettings.map((es) =>
      mergeIdsIntoEquipmentSetting(es, getIdFromCid, errorContext, valuableServerData)
    ),
  };
}

function mergeIdsIntoDataParameterSetting(
  dataParameterSetting: ProcedureDataParameterSetting,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object,
  valuableServerData: ValuableProcedureServerData
): ProcedureDataParameterSetting {
  const dataParameterVersion = assignId(
    dataParameterSetting.dataParameterVersion,
    getIdFromCid,
    {...errorContext, loadingInfo: 'dataParameter version'},
    // We need to ensure that the new version ID overwrites any prior version ID
    {forceAssign: true}
  );

  return {
    dataParameterVersion: {
      ...dataParameterVersion,
      restValueField: valuableServerData.parameterPlusSchemaFields[dataParameterVersion.id],
      procedureDataParameterId:
        valuableServerData.parameterVersionIdToParameterIdMap[dataParameterVersion.id],
    },
    values: dataParameterSetting.values.map((pv) =>
      mergeIdsIntoParameterPlannedValue(pv, getIdFromCid, {
        ...errorContext,
        loadingInfo: 'dataParameter planned value',
      })
    ),
  };
}

function mergeIdsIntoMaterialInputSetting(
  materialInputSetting: ProcedureMaterialInputSetting,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object,
  valuableServerData: ValuableProcedureServerData
): ProcedureMaterialInputSetting {
  const materialInputVersion = assignId(
    materialInputSetting.materialInputVersion,
    getIdFromCid,
    {...errorContext, loadingInfo: 'materialInput version'},
    {forceAssign: true}
  );
  return {
    materialInputVersion: {
      ...materialInputVersion,
      restMaterialField: valuableServerData.parameterPlusSchemaFields[materialInputVersion.id],
      // See extractValuableDataFromServerResponse() for where this modified ID is set up
      restAmountField: valuableServerData.parameterPlusSchemaFields[`${materialInputVersion.id}-amt`],
      procedureMaterialInputId:
        valuableServerData.parameterVersionIdToParameterIdMap[materialInputVersion.id],
    },
    values: materialInputSetting.values.map((miv) =>
      mergeIdsIntoMaterialInputPlannedValue(miv, getIdFromCid, {
        ...errorContext,
        loadingInfo: 'materialInput planned value',
      })
    ),
  };
}

function mergeIdsIntoMaterialOutputSetting(
  materialOutputSetting: ProcedureMaterialOutputSetting,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object,
  valuableServerData: ValuableProcedureServerData
): ProcedureMaterialOutputSetting {
  const materialOutputVersion = assignId(
    materialOutputSetting.materialOutputVersion,
    getIdFromCid,
    {...errorContext, loadingInfo: 'materialOutput version'},
    {forceAssign: true}
  );
  return {
    materialOutputVersion: {
      ...materialOutputVersion,
      restMaterialField: valuableServerData.parameterPlusSchemaFields[materialOutputVersion.id],
      procedureMaterialOutputId:
        valuableServerData.parameterVersionIdToParameterIdMap[materialOutputVersion.id],
    },
  };
}

function getEquipmentAllowedModels(
  allowedEquipmentModels: JSONScalar | null,
  dataType: JSONScalar
): EquipmentTypedModel {
  // For equipment created before equipment models feature flag is set, an empty typed value is generated
  if (allowedEquipmentModels == null) {
    return dataType.type === FieldType.TEXT
      ? {type: FieldType.TEXT, value: ''}
      : {
          type: FieldType.BIOENTITY_LINK,
          value: '',
          targetFiles: [],
        };
  }
  return allowedEquipmentModels;
}

function mergeIdsIntoEquipmentSetting(
  equipmentSetting: ProcedureEquipmentSetting,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object,
  valuableServerData: ValuableProcedureServerData
): ProcedureEquipmentSetting {
  const equipmentVersion = assignId(
    equipmentSetting.equipmentVersion,
    getIdFromCid,
    {...errorContext, loadingInfo: 'equipment version'},
    {forceAssign: true}
  );

  return {
    equipmentVersion: {
      ...equipmentVersion,
      restEquipmentField: valuableServerData.parameterPlusSchemaFields[equipmentVersion.id],
      models: getEquipmentAllowedModels(
        equipmentSetting.equipmentVersion.models,
        equipmentSetting.equipmentVersion.dataType
      ),
      procedureEquipmentId: valuableServerData.parameterVersionIdToParameterIdMap[equipmentVersion.id],
    },
    values: equipmentSetting.values.map((ev) =>
      mergeIdsIntoEquipmentPlannedValue(ev, getIdFromCid, {
        ...errorContext,
        loadingInfo: 'equipment planned value',
      })
    ),
  };
}

function mergeIdsIntoParameterPlannedValue(
  parameterPlannedValue: ProcedureDataParameterPlannedValue,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object
): ProcedureDataParameterPlannedValue {
  return assignId(parameterPlannedValue, getIdFromCid, {
    ...errorContext,
    loadingInfo: 'data parameter planned value',
  });
}

function mergeIdsIntoMaterialInputPlannedValue(
  materialInputPlannedValue: ProcedureMaterialInputPlannedValue,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object
): ProcedureMaterialInputPlannedValue {
  return assignId(materialInputPlannedValue, getIdFromCid, {
    ...errorContext,
    loadingInfo: 'material input planned value',
  });
}

function mergeIdsIntoEquipmentPlannedValue(
  equipmentPlannedValue: ProcedureEquipmentPlannedValue,
  getIdFromCid: (cid: string, errorContext: object) => string,
  errorContext: object
): ProcedureEquipmentPlannedValue {
  return assignId(equipmentPlannedValue, getIdFromCid, {
    ...errorContext,
    loadingInfo: 'equipment planned value',
  });
}

function mergeIdsIntoParameterUsages(
  parameterUsages: ParameterUsages,
  stepNamesAndIdsByCid: {[stepCid: string]: {name: string; id: string}}
): ParameterUsages {
  return mapObject(
    parameterUsages,
    (usages) =>
      usages?.map((singleUsage) => ({
        ...singleUsage,
        stepId: stepNamesAndIdsByCid[singleUsage.stepCid]?.id ?? singleUsage.stepId,
        stepName: stepNamesAndIdsByCid[singleUsage.stepId]?.name ?? singleUsage.stepName,
      })) ?? undefined
  );
}

function mergeIdsIntoMethodNode(
  methodNode: MethodNode,
  getIdFromCid: (cid: string, errorContext: object) => string,
  valuableServerData: ValuableProcedureServerData
): MethodNode {
  const errorContext = {methodId: methodNode.id, methodCid: methodNode.cid};

  const stepNamesAndIdsByCid = objectFromEntries(
    methodNode.stepGroups.flatMap((sg) =>
      sg.steps.map((step) => {
        const stepCid = step.cid;
        return [
          stepCid,
          {
            name: step.name,
            id: getIdFromCid(stepCid, {
              ...errorContext,
              loadingInfo: 'steps',
            }),
          },
        ] as [string, {name: string; id: string}];
      })
    )
  );

  return {
    ...methodNode,
    id: getIdFromCid(methodNode.cid, errorContext),
    stepGroups: methodNode.stepGroups.map((sg) =>
      mergeIdsIntoStepGroup(sg, getIdFromCid, errorContext, valuableServerData)
    ),
    ...mergeIdsIntoAllParameterSettings(methodNode, getIdFromCid, errorContext, valuableServerData),
    parameterUsages: mergeIdsIntoParameterUsages(methodNode.parameterUsages, stepNamesAndIdsByCid),
  };
}

/**
 * When a PoW procedure is saved, the API returns a new Cid-Id map. We integrate any new IDs for database objects
 * just created into the procedure.
 */
export default function mergeIdsIntoPowProcedureFlowchartConfig(
  flowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema,
  cidToIdMap: CidIdMap,
  valuableServerData: ValuableProcedureServerData
): FlowchartConfigWithMetadataAndRootTaskSchema {
  const getIdFromCid = (cid: string, errorContext: object): string => {
    const id = cidToIdMap[cid];
    assert(id != null && typeof id === 'string', 'Missing id in procedure', {
      cidWithMissingId: cid,
      cidToIdMap,
      ...errorContext,
    });
    return String(id);
  };

  const updatedNodes = flowchartConfig.staticFlowchart.nodesStaticData.map((node) => {
    if (!isStaticTaskSchemaNodeData(node) || !isMethodNode(node.taskDefinitionVersionSpec)) {
      return node;
    }
    const methodNodeData = node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData;
    if (methodNodeData == null) {
      return node;
    }

    const updatedNode = mergeIdsIntoMethodNode(methodNodeData, getIdFromCid, valuableServerData);
    return produce(node, (draftNode) => {
      draftNode.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData = updatedNode;
    });
  });

  return {
    ...flowchartConfig,
    staticFlowchart: {
      ...flowchartConfig.staticFlowchart,
      nodesStaticData: updatedNodes,
    },
  };
}
